// Adapter from /admin/fatigue/workforce to the scoring engine's roster.
//
// The API supplies only what the wearable data supports. Every input the source
// cannot give (intraday HR, rota, assessment, EDA) is set to a neutral value that
// makes the dependent formula inert, and `coverage` tells the screens not to
// present those scores at all.

import { calcFED } from './scores';
import { replaceRoster } from './data';
import type { Group, RawWorker, Region, Worker } from './data';

export interface WireWorker {
  id: string;
  name: string;
  role: string;
  group: number;
  asOf: string;
  hrv: number;
  rhr: number;
  slp: number;
  hrv_b: number;
  rhr_b: number;
  slp_b: number;
  slp7: number[];
  slp_efficiency: number | null;
  slp_onset_drift: number | null;
  baselineDays: number;
  coverage: NonNullable<RawWorker['coverage']>;
  history: { day: string; hrv: number | null; rhr: number | null; slp: number | null }[];
}

export interface WireWorkforce {
  asOf: string | null;
  enrolled: number;
  regions: { id: string; name: string; groups: number[] }[];
  groups: Group[];
  workers: WireWorker[];
}

const REGION_COLOURS = ['#2f6fd0', '#d9622f', '#1a9a6c', '#7c70d6', '#d46a94'];

const mean = (v: number[]) => (v.length ? v.reduce((a, b) => a + b, 0) / v.length : null);

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('') || 'W';

/** Weekly FED for the last 12 weeks, scored against each week's own trailing 28-day baseline. */
function weeklyFed(w: WireWorker): number[] {
  const byDay = new Map(w.history.map((h) => [h.day, h]));
  const anchor = Date.parse(w.asOf);
  const out: number[] = [];
  for (let wk = 11; wk >= 0; wk--) {
    const end = anchor - wk * 7 * 86_400_000;
    const at = (offset: number) => byDay.get(new Date(end - offset * 86_400_000).toISOString().slice(0, 10));
    const today = at(0);
    if (!today || today.hrv === null || today.rhr === null || today.slp === null) continue;
    const prior = Array.from({ length: 28 }, (_, i) => at(i + 1)).filter((d): d is NonNullable<typeof d> => !!d);
    const hrvB = mean(prior.map((d) => d.hrv).filter((v): v is number => v !== null));
    const rhrB = mean(prior.map((d) => d.rhr).filter((v): v is number => v !== null));
    const slpB = mean(prior.map((d) => d.slp).filter((v): v is number => v !== null));
    if (hrvB === null || rhrB === null || slpB === null || prior.length < 7) continue;
    const slp7 = Array.from({ length: 7 }, (_, i) => at(i)?.slp)
      .filter((v): v is number => typeof v === 'number')
      .reverse();
    out.push(
      calcFED({
        hrv: today.hrv,
        rhr: today.rhr,
        slp: today.slp,
        hrv_b: hrvB,
        rhr_b: rhrB,
        slp_b: slpB,
        slp7,
      } as Worker),
    );
  }
  return out;
}

export function loadLiveRoster(data: WireWorkforce): void {
  const used = new Set<string>();
  const regions: Record<string, Region> = {};
  data.regions.forEach((r, i) => {
    regions[r.id] = { name: r.name, color: REGION_COLOURS[i % REGION_COLOURS.length], groups: r.groups };
  });

  const workers: RawWorker[] = data.workers.map((w) => {
    let init = initialsOf(w.name);
    for (let n = 2; used.has(init); n++) init = `${initialsOf(w.name)}${n}`;
    used.add(init);
    const fed12 = weeklyFed(w);
    return {
      init,
      name: w.name,
      group: w.group,
      role: w.role,
      roleType: 'mixed',
      // Static onboarding fields only feed PLI/PSI, which are not scored on live data.
      age: 40,
      weight_kg: 75,
      gender: 'M',
      hrv: w.hrv,
      rhr: w.rhr,
      slp: w.slp,
      hrv_b: w.hrv_b,
      rhr_b: w.rhr_b,
      slp_b: w.slp_b,
      slp7: w.slp7.length ? w.slp7 : [w.slp],
      slp_efficiency: w.slp_efficiency ?? 88,
      slp_onset_drift: w.slp_onset_drift ?? 0,
      // Not sourceable from daily wearable data — neutral, and flagged via `coverage`.
      restHrs: 0,
      consec: 0,
      edaScore: 0,
      assessScore: 0,
      hr_work: 0,
      hr_work_b: 0,
      hr_rec: 0,
      hr_rec_b: 0,
      hrv_pre: w.hrv,
      hrv_post: w.hrv,
      shiftHrs: 0,
      normalHrs: 40,
      otHrs: 0,
      driveKm: 0,
      driveDays: 0,
      wtrFlag: false,
      inWorkFed: 0,
      outWorkFed: 0,
      hrv_peak_offset: 0,
      rhr_curve_rmse: 0,
      fed12: fed12.length ? fed12 : [],
      shifts: [],
      coverage: w.coverage,
    };
  });

  replaceRoster(workers, data.groups, regions);

  // Anchor the last trend point to the scored FED, as the demo roster does.
  for (const w of workers as Worker[]) {
    if (!w.fed12.length) w.fed12 = [w.fed];
    else w.fed12[w.fed12.length - 1] = w.fed;
  }
}
